/* ========================================================== <keis-faq-hub>
   The FAQ page's search box and topic chips (sections/faq-hub.liquid).

   Everything is already in the page; this only arranges it.

   With no search, questions sit in their topics and a chip shows just one
   topic (or the product questions), in place: the page does not move.

   With a search, the questions are ranked by keis-faqsearch.js (whole
   questions, synonyms, typos) and shown as one list, best answer first, each
   labelled with its topic or product. When the best answer is clearly ahead
   it opens, so the answer is there without a click. A chip still narrows the
   list to its topic. Clearing the search puts every question back where it
   came from.

   ?q=… in the address fills the box (the support page's search lands here);
   when it is exactly one question, that answer opens and comes into view.
   #topic-name picks that topic's chip.

   Insights (keis-insights.js): a search is counted once the visitor is done
   with it (they open an answer, leave the box, press Enter or leave the
   page), never letter by letter, and as "no answer" when nothing matched.
   Answers opened and topic chips used are counted too, by their English.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('keis-faq-hub')) return;

  var norm = function (s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  };

  class FaqHub extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.input = this.querySelector('[data-faq-search]');
      this.status = this.querySelector('[data-faq-status]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.groupsBox = this.querySelector('.keis-faqhub__groups');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-faq-filter]'));
      this.filter = 'all';

      /* Each question, where it lives, and what it is about. A marker left
         in its place lets it go back after a search has moved it. */
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-faq-item]')).map(function (el) {
        var group = el.closest('[data-faq-group]');
        var title = group && group.querySelector('.keis-faqhub__title');
        var product = el.closest('[data-faq-product]');
        var ctx = product ? product.querySelector('.keis-faqhub__product-title').textContent.trim()
                          : (title ? title.textContent.trim() : '');
        var q = el.querySelector('.keis-accordion__q');
        var qText = q ? q.textContent.trim() : '';
        var panel = el.querySelector('.keis-accordion__panel');
        if (q && ctx) {
          var tag = document.createElement('span');
          tag.className = 'keis-faqhub__ctx';
          tag.textContent = ctx;
          q.appendChild(tag);
        }
        var home = document.createComment('');
        el.parentNode.insertBefore(home, el);
        return {
          el: el, home: home, group: group ? group.getAttribute('data-faq-group') : '',
          doc: { q: qText, a: panel ? panel.textContent : '', ctx: ctx + ' ' + (title ? title.textContent : '') }
        };
      });
      this.index = window.KeisFaqSearch ? window.KeisFaqSearch.index(this.items.map(function (it) { return it.doc; })) : null;

      this.results = document.createElement('div');
      this.results.className = 'keis-faqhub__results keis-accordion';
      this.results.hidden = true;
      if (this.groupsBox) this.groupsBox.insertBefore(this.results, this.groupsBox.firstChild);

      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          self.pick(chip.getAttribute('data-faq-filter'));
          self.commitSearch();
          if (chip.getAttribute('data-track')) self.track('topic', chip.getAttribute('data-track'));
        });
      });
      this.items.forEach(function (it) {
        var summary = it.el.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('click', function () {
          if (it.el.open) return;   /* closing it */
          self.commitSearch();
          self.track('open', it.el.getAttribute('data-track') || it.doc.q);
        });
      });
      var timer = null;
      if (this.input) {
        this.input.addEventListener('input', function () {
          clearTimeout(timer);
          timer = setTimeout(function () { self.apply(true); }, 140);
        });
        this.input.addEventListener('blur', function () { self.commitSearch(); });
        this.input.addEventListener('keydown', function (e) { if (e.key === 'Enter') self.commitSearch(); });
        if (window.keisTrack && window.keisTrack.beforeLeave) window.keisTrack.beforeLeave(function () { self.commitSearch(); });
      }

      var params = new URLSearchParams(window.location.search);
      if (this.input && params.get('q')) this.input.value = params.get('q');
      var hash = decodeURIComponent(window.location.hash.slice(1));
      var target = hash && this.querySelector('[data-faq-group][id="' + hash.replace(/"/g, '') + '"]');
      if (target) this.pick(target.getAttribute('data-faq-group'));
      else this.apply(false);

      /* Arriving from a support-page suggestion: the address carries the
         question itself, so open that answer and bring it into view. */
      var asked = norm(params.get('q') || '').trim();
      /* A search that arrives from the support page counts here, where its
         answers are; a suggestion followed there was counted there. */
      this.counted = '';
      if (asked) {
        var exact = this.items.find(function (it) { return norm(it.doc.q) === asked; });
        if (exact) this.counted = asked;
        if (exact) {
          exact.el.open = true;
          setTimeout(function () { exact.el.scrollIntoView({ block: 'center' }); }, 60);
        }
      }
    }

    track(metric, key) {
      if (window.keisTrack) window.keisTrack('support', metric, key);
    }

    /* The search in the box, counted once, when the visitor is done with it. */
    commitSearch() {
      var query = this.input ? this.input.value.trim() : '';
      var n = norm(query).replace(/\s+/g, ' ').trim();
      if (n.length < 3 || n === this.counted || !this.index) return;
      this.counted = n;
      this.track('search', query);
      if (!window.KeisFaqSearch.search(this.index, query).length) this.track('search_none', query);
    }

    pick(filter) {
      this.filter = filter || 'all';
      this.chips.forEach(function (c) {
        c.setAttribute('aria-pressed', c.getAttribute('data-faq-filter') === filter ? 'true' : 'false');
      });
      /* The list changes in place; the page does not move. */
      this.apply(false);
    }

    /* Every question back in its topic, in its own order. */
    restore() {
      this.items.forEach(function (it) {
        if (it.el.previousSibling !== it.home) it.home.parentNode.insertBefore(it.el, it.home.nextSibling);
        if (it.autoOpened) { it.el.open = false; it.autoOpened = false; }
      });
      this.results.hidden = true;
      this.classList.remove('is-searching');
    }

    apply(fromTyping) {
      var query = this.input ? this.input.value.trim() : '';
      var filter = this.filter;
      var inScope = function (it) { return filter === 'all' || it.group === filter; };
      var shown = 0;

      this.restore();

      if (query && this.index) {
        var self = this;
        var ranked = window.KeisFaqSearch.search(this.index, query)
          .map(function (r) { return { it: self.items[r.i], score: r.score }; })
          .filter(function (r) { return inScope(r.it); });
        this.items.forEach(function (it) { it.el.hidden = true; });
        ranked.forEach(function (r) {
          r.it.el.hidden = false;
          self.results.appendChild(r.it.el);
        });
        shown = ranked.length;
        /* The best answer opens when it is clearly the best. */
        if (ranked.length && (ranked.length === 1 || ranked[0].score >= ranked[1].score * 1.25)) {
          if (!ranked[0].it.el.open) { ranked[0].it.el.open = true; ranked[0].it.autoOpened = true; }
        }
        this.results.hidden = !shown;
        this.classList.add('is-searching');
        Array.prototype.forEach.call(this.querySelectorAll('[data-faq-group]'), function (g) { g.hidden = true; });
      } else {
        this.items.forEach(function (it) {
          var show = inScope(it);
          it.el.hidden = !show;
          if (show) shown += 1;
        });
        /* Hide the containers left empty, innermost first. */
        ['[data-faq-product]', '[data-faq-range]', '[data-faq-group]'].forEach(function (sel) {
          Array.prototype.forEach.call(this.querySelectorAll(sel), function (box) {
            box.hidden = !box.querySelector('[data-faq-item]:not([hidden])');
          });
        }, this);
      }

      if (this.empty) this.empty.hidden = shown > 0;
      if (this.status) {
        var one = this.getAttribute('data-result-one') || '1 answer';
        var many = this.getAttribute('data-result-other') || '[count] answers';
        this.status.textContent = query ? (shown === 1 ? one : many.replace('[count]', shown)) : '';
      }
      if (fromTyping && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
        window.history.replaceState(null, '', url.toString());
      }
    }
  }

  customElements.define('keis-faq-hub', FaqHub);
})();
